/**
 * What the market was doing when you entered.
 *
 * The panel behind the claim in `docs/23`: every journal draws a chart with
 * your fills on it, and none of them can say where those fills sat relative to
 * the structure that existed at the time — because a journal-first product
 * holds fills and nothing else.
 *
 * ## Everything here is a distance, never a verdict
 *
 * "Two ticks above the prior value-area high" is arithmetic. "You chased" is
 * advice, and CLAUDE.md's non-negotiable 4 forbids it. The reader draws the
 * conclusion, which is both safer and more persuasive than being handed one.
 *
 * ## It says when it cannot say
 *
 * Three states where a lesser panel would improvise: not enough bars, no bars
 * for that period at all, and a fill the bar series disagrees with. Each gets
 * its own sentence, because "no data" and "your data is inconsistent" send a
 * reader to completely different places.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { fmtNum } from '@aegis/api-client';
import type { TradeContextDto } from '@aegis/api-client';
import { api } from '../lib/api.js';

/** Where in a bar's range a fill sat, in words a trader would use. */
function rangeWord(percentile: number): string {
  if (percentile >= 90) return 'at the top of';
  if (percentile >= 70) return 'in the upper third of';
  if (percentile > 30) return 'in the middle of';
  if (percentile > 10) return 'in the lower third of';
  return 'at the bottom of';
}

export function TradeContextPanel({ accountId, tradeId, precision }: {
  accountId: string;
  tradeId: string;
  precision: number;
}): JSX.Element {
  const [ctx, setCtx] = useState<TradeContextDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setCtx(null);
    setError(null);
    void api.tradeContext(accountId, tradeId)
      .then((c) => { if (live) setCtx(c); })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : 'Could not rebuild the context.');
      });
    return () => { live = false; };
  }, [accountId, tradeId]);

  if (error) return <p className="ctx-note">{error}</p>;
  if (!ctx) return <p className="ctx-note">Rebuilding the market around this trade…</p>;

  if (ctx.reason === 'no_history_at_that_time') {
    return (
      <div className="trade-context">
        <h4>At the moment you entered</h4>
        <p className="ctx-note">
          AEGIS has no {ctx.timeframe} bars covering that date, so there is nothing
          honest to say about what the market was doing. Nothing is inferred from
          the bars it does hold — those belong to a different period.
        </p>
      </div>
    );
  }

  if (ctx.reason === 'too_few_bars') {
    return (
      <div className="trade-context">
        <h4>At the moment you entered</h4>
        <p className="ctx-note">
          Only {ctx.barCount} bars were available before this entry — too few to
          describe market structure without guessing.
        </p>
      </div>
    );
  }

  return (
    <div className="trade-context">
      <h4>At the moment you entered</h4>

      <div className="ctx-head">
        {ctx.regime && <span className="ctx-regime">{ctx.regime.replace(/_/g, ' ')}</span>}
        {ctx.volatilityPercentile !== null && (
          <span className="ctx-vol">
            volatility in the {Math.round(ctx.volatilityPercentile)}
            <sup>{ordinal(Math.round(ctx.volatilityPercentile))}</sup> percentile
          </span>
        )}
        <span className="ctx-src mono">
          {ctx.barCount} × {ctx.timeframe}
        </span>
      </div>

      {/* The execution-quality question nobody's journal asks. */}
      {ctx.entryInBar && (
        <p className="ctx-line">
          Your entry filled <strong>{rangeWord(ctx.entryInBar.percentile)}</strong> the
          {' '}{ctx.timeframe} bar it landed in
          {' '}<span className="mono">
            ({fmtNum(ctx.entryInBar.barLow, precision)}–{fmtNum(ctx.entryInBar.barHigh, precision)})
          </span>.
        </p>
      )}
      {ctx.exitInBar && (
        <p className="ctx-line">
          Your exit filled <strong>{rangeWord(ctx.exitInBar.percentile)}</strong> its bar
          {' '}<span className="mono">
            ({fmtNum(ctx.exitInBar.barLow, precision)}–{fmtNum(ctx.exitInBar.barHigh, precision)})
          </span>.
        </p>
      )}
      {!ctx.entryInBar && (
        <p className="ctx-note">
          The fill price sits outside the bar it is timestamped in, so AEGIS will not
          claim where in the range it landed. That is a disagreement between your
          fills and the bar series, not an execution fact.
        </p>
      )}

      {ctx.nearest.length > 0 && (
        <>
          <h5>Where the entry sat</h5>
          <ul className="ctx-levels">
            {ctx.nearest.map((l) => (
              <li key={l.id}>
                <span className="ctx-ticks mono">
                  {l.ticks === 0 ? 'at' : `${Math.abs(l.ticks)}t ${l.side}`}
                </span>
                <span className="ctx-label">{l.label}</span>
                <span className="ctx-price mono">{fmtNum(l.price, precision)}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {ctx.releases.length > 0 && (
        <>
          <h5>Macro around the entry</h5>
          <ul className="ctx-releases">
            {ctx.releases.map((r) => (
              <li key={`${r.sourceId}${r.releasedAt}`} className={`imp-${r.importance}`}>
                <span className="ctx-when mono">
                  {r.minutesFromEntry === 0 ? 'at entry'
                    : r.minutesFromEntry < 0 ? `${Math.abs(r.minutesFromEntry)}m before`
                      : `${r.minutesFromEntry}m after`}
                </span>
                <span className="ctx-title">{r.title}</span>
                <span className="ctx-figures mono">
                  {r.actual}{r.unit}
                  {r.consensus !== null && <> vs {r.consensus}{r.unit}</>}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {/*
        Absence stated rather than implied. An empty list reads as "nothing
        happened", which is a different claim from "this was never recorded".
      */}
      {!ctx.headlinesAvailable && (
        <p className="ctx-note">
          Headlines are not archived, so only scheduled macro releases can be
          rebuilt for a past trade. {ctx.releases.length === 0 && 'None affected this instrument in the hour around the entry.'}
        </p>
      )}
    </div>
  );
}

function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return 'th';
  switch (n % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}
